import {
  Bookmark as BookmarkIcon,
  BookmarkBorder as BookmarkBorderIcon,
  DeleteOutline as DeleteOutlineIcon,
} from '@mui/icons-material';
import { Box, Stack, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';

import { clearImpactCo2Answers } from '@/domain/impact-co2/actions/clear-impact-co2-answers.action';
import { getImpactCo2Answers } from '@/domain/impact-co2/actions/get-impact-co2-answers.action';
import { TourEvent } from '@/domain/project-tour-ontour/entities/project-tour-ontour-event.entity';
import {
  TourTravel,
  TourTravelConnexionTypeWithEvent,
} from '@/domain/project-tour-ontour/entities/project-tour-ontour-travel.entity';
import { selectProjectTourOnTourEvents } from '@/domain/project-tour-ontour/project-tour-ontour.selectors';
import { FSModal } from '@/ui/components/modal/FSModal';
import { useAppDispatch, useAppSelector } from '@/ui/hook/store';
import { translate } from '@/ui/i18n';
import { formatDate } from '@/ui/utils/format-date';

import { FavoriteTravelGroupOption } from '../../utils/tour-travel-favorite-group';
import { AddTravelSplitButton } from './AddTravelSplitButton';
import { TourTravelView } from './TourTravelView';

export function ModalTourTravelGroupForm({
  open,
  onClose,
  tourTravels,
  initialIndex = 0,
  onClickAdd,
  onDelete,
  favoriteIds,
  onToggleFavorite,
  favoriteTravels,
  onCopyTravel,
  isWindowFavorite,
  onToggleWindowFavorite,
  favoriteGroups,
  onCopyGroup,
}: {
  open: boolean;
  onClose: () => void;
  tourTravels: TourTravel[];
  initialIndex?: number;
  onClickAdd?: () => void;
  onDelete: (tourTravelId: number) => void;
  favoriteIds: Set<number>;
  onToggleFavorite: (id: number) => void;
  favoriteTravels: TourTravel[];
  onCopyTravel: (sourceTourTravel: TourTravel) => void;
  isWindowFavorite: boolean;
  onToggleWindowFavorite: () => void;
  favoriteGroups: FavoriteTravelGroupOption[];
  onCopyGroup: (travels: TourTravel[]) => void;
}) {
  const dispatch = useAppDispatch();
  const [activeIndex, setActiveIndex] = useState(initialIndex);
  const prevTravelCountRef = useRef(tourTravels.length);

  useEffect(() => {
    if (!open || tourTravels.length === 0) return;
    setActiveIndex(initialIndex);
    dispatch(getImpactCo2Answers({ projectId: tourTravels[initialIndex].id }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) {
      prevTravelCountRef.current = tourTravels.length;
      return;
    }
    if (tourTravels.length > prevTravelCountRef.current) {
      const newIndex = tourTravels.length - 1;
      dispatch(clearImpactCo2Answers());
      dispatch(getImpactCo2Answers({ projectId: tourTravels[newIndex].id }));
      setActiveIndex(newIndex);
    } else if (tourTravels.length < prevTravelCountRef.current) {
      if (tourTravels.length === 0) {
        handleClose();
      } else {
        const safeIndex = Math.min(activeIndex, tourTravels.length - 1);
        dispatch(clearImpactCo2Answers());
        dispatch(getImpactCo2Answers({ projectId: tourTravels[safeIndex].id }));
        setActiveIndex(safeIndex);
      }
    }
    prevTravelCountRef.current = tourTravels.length;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourTravels.length]);

  const onTabChange = (_: unknown, newIndex: number) => {
    dispatch(clearImpactCo2Answers());
    dispatch(getImpactCo2Answers({ projectId: tourTravels[newIndex].id }));
    setActiveIndex(newIndex);
  };

  const handleClose = () => {
    const sequenceId = tourTravels[0]?.sequenceId;
    dispatch(clearImpactCo2Answers());
    if (sequenceId !== undefined) {
      dispatch(getImpactCo2Answers({ projectId: sequenceId }));
    }
    onClose();
  };

  const safeActiveIndex = Math.min(
    activeIndex,
    Math.max(0, tourTravels.length - 1),
  );
  const activeTourTravel = tourTravels[safeActiveIndex];

  return (
    <FSModal
      open={open}
      onClose={handleClose}
      maxWidth={'78rem'}
      showCloseButton
    >
      <ModalGroupTitle
        tourTravels={tourTravels}
        isWindowFavorite={isWindowFavorite}
        onToggleWindowFavorite={onToggleWindowFavorite}
      />
      <Box sx={{ height: '1rem' }} />
      <Stack sx={{ flexDirection: 'row', gap: 2, minHeight: '32rem' }}>
        <Stack
          sx={{
            borderRight: 1,
            borderColor: 'divider',
            width: '14rem',
            flexShrink: 0,
            overflow: 'hidden',
          }}
        >
          <Tabs
            orientation="vertical"
            value={safeActiveIndex}
            onChange={onTabChange}
            sx={{
              '& .MuiTab-root': { alignItems: 'flex-start', textAlign: 'left' },
            }}
          >
            {tourTravels.map((travel, index) => (
              <Tab
                key={travel.id}
                label={
                  <TravelTabLabel
                    tourTravel={travel}
                    index={index}
                    isFavorite={favoriteIds.has(travel.id)}
                    onToggleFavorite={() => onToggleFavorite(travel.id)}
                    onDelete={() => onDelete(travel.id)}
                  />
                }
              />
            ))}
          </Tabs>
          {onClickAdd && (
            <Box sx={{ px: 1, pt: 1 }}>
              <AddTravelSplitButton
                onClickAdd={onClickAdd}
                favoriteTravels={favoriteTravels}
                onCopyTravel={onCopyTravel}
                favoriteGroups={favoriteGroups}
                onCopyGroup={onCopyGroup}
              />
            </Box>
          )}
        </Stack>
        {activeTourTravel && (
          <TourTravelView
            key={activeTourTravel.id}
            tourTravel={activeTourTravel}
          />
        )}
      </Stack>
    </FSModal>
  );
}

function TravelTabLabel({
  tourTravel,
  index,
  isFavorite,
  onToggleFavorite,
  onDelete,
}: {
  tourTravel: TourTravel;
  index: number;
  isFavorite: boolean;
  onToggleFavorite: () => void;
  onDelete: () => void;
}) {
  const tourEvents = useAppSelector(selectProjectTourOnTourEvents);
  const fromEvent = tourEvents.find((e) => e.id === tourTravel.fromEventId);
  const toEvent = tourEvents.find((e) => e.id === tourTravel.toEventId);

  const fromName = getFromName(tourTravel, fromEvent);
  const toName = getToName(tourTravel, toEvent);

  const routeLabel = [fromName, toName].filter(Boolean).join(' / ');

  return (
    <Stack
      sx={{
        alignItems: 'flex-start',
        gap: 0.25,
        py: 0.25,
        width: '100%',
        overflow: 'hidden',
      }}
    >
      <Stack
        sx={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 0.5,
          width: '100%',
        }}
      >
        <Typography
          noWrap
          sx={{
            fontSize: '0.875rem',
            fontWeight: 600,
            textTransform: 'none',
            flexGrow: 1,
          }}
        >
          {tourTravel.name || `#${index + 1}`}
        </Typography>
        <FavoriteToggle
          isFavorite={isFavorite}
          onToggleFavorite={onToggleFavorite}
        />
        <DeleteAction onDelete={onDelete} />
      </Stack>
      {routeLabel && (
        <Typography
          noWrap
          sx={{
            fontSize: '0.75rem',
            color: 'text.secondary',
            textTransform: 'none',
            width: '100%',
          }}
        >
          {routeLabel}
        </Typography>
      )}
      {tourTravel.travelDistance > 0 && (
        <Typography
          sx={{
            fontSize: '0.75rem',
            color: 'text.secondary',
            textTransform: 'none',
          }}
        >
          {tourTravel.travelDistance} km
        </Typography>
      )}
    </Stack>
  );
}

function FavoriteToggle({
  isFavorite,
  onToggleFavorite,
}: {
  isFavorite: boolean;
  onToggleFavorite: () => void;
}) {
  const onClick = (event: React.MouseEvent<HTMLSpanElement>) => {
    event.stopPropagation();
    onToggleFavorite();
  };

  return (
    <Tooltip
      title={translate('ProjectsTour.TourTravelFavorite.tooltip')}
      placement="top"
    >
      <Box
        component="span"
        onClick={onClick}
        sx={{
          display: 'inline-flex',
          cursor: 'pointer',
          color: isFavorite ? 'warning.main' : 'action.disabled',
          '&:hover': { color: 'warning.main' },
        }}
      >
        {isFavorite ? (
          <BookmarkIcon fontSize="small" />
        ) : (
          <BookmarkBorderIcon fontSize="small" />
        )}
      </Box>
    </Tooltip>
  );
}

function DeleteAction({ onDelete }: { onDelete: () => void }) {
  const onClick = (event: React.MouseEvent<HTMLSpanElement>) => {
    event.stopPropagation();
    onDelete();
  };

  return (
    <Tooltip title={translate('Form.delete')} placement="top">
      <Box
        component="span"
        onClick={onClick}
        sx={{
          display: 'inline-flex',
          cursor: 'pointer',
          color: 'action.disabled',
          '&:hover': { color: 'error.main' },
        }}
      >
        <DeleteOutlineIcon fontSize="small" />
      </Box>
    </Tooltip>
  );
}

function WindowFavoriteToggle({
  isWindowFavorite,
  onToggleWindowFavorite,
}: {
  isWindowFavorite: boolean;
  onToggleWindowFavorite: () => void;
}) {
  const label = isWindowFavorite
    ? translate('ProjectsTour.TourTravelFavorite.window-marked')
    : translate('ProjectsTour.TourTravelFavorite.mark-window');

  return (
    <Tooltip
      title={translate('ProjectsTour.TourTravelFavorite.mark-window-tooltip')}
      placement="top"
    >
      <Stack
        component="button"
        type="button"
        onClick={onToggleWindowFavorite}
        sx={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: 0.5,
          flexShrink: 0,
          border: 'none',
          background: 'none',
          cursor: 'pointer',
          padding: 0,
          fontSize: '0.8rem',
          color: isWindowFavorite ? 'warning.main' : 'text.secondary',
          '&:hover': { color: 'warning.main' },
        }}
      >
        {isWindowFavorite ? (
          <BookmarkIcon fontSize="small" />
        ) : (
          <BookmarkBorderIcon fontSize="small" />
        )}
        {label}
      </Stack>
    </Tooltip>
  );
}

function getFromName(
  tourTravel: TourTravel,
  fromEvent: TourEvent | undefined,
): string | undefined {
  switch (
    tourTravel.connexionTypeWithEvent as TourTravelConnexionTypeWithEvent
  ) {
    case 'incoming':
      return tourTravel.fromPosition?.properties.name;
    case 'outgoing':
    case 'direct':
      return fromEvent?.position?.properties.name;
  }
}

function getToName(
  tourTravel: TourTravel,
  toEvent: TourEvent | undefined,
): string | undefined {
  switch (
    tourTravel.connexionTypeWithEvent as TourTravelConnexionTypeWithEvent
  ) {
    case 'incoming':
    case 'direct':
      return toEvent?.position?.properties.name;
    case 'outgoing':
      return tourTravel.toPosition?.properties.name;
  }
}

function ModalGroupTitle({
  tourTravels,
  isWindowFavorite,
  onToggleWindowFavorite,
}: {
  tourTravels: TourTravel[];
  isWindowFavorite: boolean;
  onToggleWindowFavorite: () => void;
}) {
  const tourEvents = useAppSelector(selectProjectTourOnTourEvents);
  const firstTravel = tourTravels[0];
  if (!firstTravel) return null;

  const typeLabel = getModalTitle(firstTravel);
  const eventInfo = getLinkedEventInfo(firstTravel, tourEvents);

  return (
    <Stack sx={{ gap: 0.25 }}>
      <Stack sx={{ flexDirection: 'row', alignItems: 'center', gap: 1.5 }}>
        <Typography sx={{ fontSize: '1.5rem', fontWeight: 700 }}>
          {typeLabel}
        </Typography>
        <WindowFavoriteToggle
          isWindowFavorite={isWindowFavorite}
          onToggleWindowFavorite={onToggleWindowFavorite}
        />
      </Stack>
      {eventInfo && (
        <Tooltip title={eventInfo}>
          <Typography
            noWrap
            sx={{
              fontSize: '0.875rem',
              color: 'text.secondary',
              maxWidth: '40rem',
            }}
          >
            {eventInfo}
          </Typography>
        </Tooltip>
      )}
    </Stack>
  );
}

function getLinkedEventInfo(
  tourTravel: TourTravel,
  tourEvents: TourEvent[],
): string | undefined {
  const linkedEventId =
    tourTravel.connexionTypeWithEvent === 'outgoing'
      ? tourTravel.fromEventId
      : tourTravel.toEventId;

  if (tourTravel.connexionTypeWithEvent === 'direct') {
    const fromEvent = tourEvents.find((e) => e.id === tourTravel.fromEventId);
    const toEvent = tourEvents.find((e) => e.id === tourTravel.toEventId);
    const fromLabel = fromEvent?.position?.properties.name ?? fromEvent?.name;
    const toLabel = toEvent?.position?.properties.name ?? toEvent?.name;
    if (!fromLabel && !toLabel) return undefined;
    return [fromLabel, toLabel].filter(Boolean).join(' → ');
  }

  const event = tourEvents.find((e) => e.id === linkedEventId);
  if (!event) return undefined;

  const location = event.position?.properties.name ?? event.name;
  const date = event.date ? formatDate(event.date) : undefined;
  return [location, date].filter(Boolean).join(' · ');
}

function getModalTitle(tourTravel: TourTravel | undefined) {
  if (!tourTravel) return '';
  switch (tourTravel.connexionTypeWithEvent) {
    case 'incoming':
      return translate('ProjectsTour.travel-incoming-title');
    case 'outgoing':
      return translate('ProjectsTour.travel-outgoing-title');
    case 'direct':
      return translate('ProjectsTour.travel-direct-title');
  }
}
